import {IconButton} from '@wandb/components';
// @todo: Migrate to 'EditableField' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import EditableField from '@wandb/weave/common/components/EditableField';
import React, {useMemo, useState} from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {useMaybeWorkspaceRefsContext} from '../../components/Workspace/WorkspaceRefsContext';
import {LoaderProxy} from '../../containers/AppLoader';
import {useRunPageHeaderQuery} from '../../generated/graphql';
import {usePartMaybe} from '../../state/views/hooks';
import * as GQLTypes from '../../types/graphql';
import {safeSessionStorage} from '../../util/localStorage';
import {useStatsigGateWorkspaceConsolidation} from '../../util/rampFeatureFlags';
import {getCustomRunName} from '../../util/runs';
import * as urls from '../../util/urls';
import {RunOverflowMenu} from './RunOverflowMenu';
import {useUpdateRun} from './useUpdateRun';

type RunPageHeaderProps = {
  readOnly: boolean;
  run?: GQLTypes.RunPageRunQuery;
  project?: GQLTypes.Project;
  entityName: string;
  projectName: string;
  runName: string;
};

const RunPageHeader = ({
  readOnly,
  run: runFromProps,
  project: projectFromProps,
  entityName,
  projectName,
  runName,
}: RunPageHeaderProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();

  const {customRunNamesRef} = useMaybeWorkspaceRefsContext() ?? {};
  const customRunNames = usePartMaybe(customRunNamesRef);
  const customRunName = getCustomRunName(runName, false, customRunNames);

  const exitUrl = useMemo(() => {
    const sourcePath = safeSessionStorage.getItem('runDetailsSourcePath');
    if (sourcePath) {
      safeSessionStorage.removeItem('runDetailsSourcePath');
      return sourcePath;
    } else {
      return urls.run({
        entityName,
        projectName,
        name: runName,
      });
    }
  }, [entityName, projectName, runName]);

  const runHeaderQuery = useRunPageHeaderQuery({
    variables: {
      entityName,
      projectName,
      runName,
    },
    skip: !isWorkspaceConsolidationEnabled,
  });
  const run = isWorkspaceConsolidationEnabled
    ? runHeaderQuery.data?.project?.run
    : runFromProps;
  const updateRun = useUpdateRun(run);

  if (isWorkspaceConsolidationEnabled && runHeaderQuery.loading) {
    return <LoaderProxy name="run-header-query-loading-loader" />;
  }

  if (!run) {
    return null;
  }

  return (
    <div className="name-header-wrapper gap-14">
      {isWorkspaceConsolidationEnabled && (
        <div className="shrink-0">
          <IconButton
            icon="close"
            variant="secondary"
            aria-label="Close run details"
            href={exitUrl}
          />
        </div>
      )}
      <div className="name-header-content gap-14">
        {isWorkspaceConsolidationEnabled && (
          <div className="flex items-center w-full gap-8">
            <EditableField
              className="name-header-consolidated"
              readOnly={readOnly}
              placeholder={runName}
              value={run.displayName ?? ''}
              save={async value => {
                const result = await updateRun({displayName: value});
                if (result.success) {
                  window.analytics?.track('Run name edited');
                } else {
                  toast.error(result.error);
                }
              }}
              spellCheck={false}
              autoSelect={false}
              setIsEditing={setIsEditing}
            />
            {isEditing && (
              <p className="rounded bg-moon-100 px-6 text-sm text-moon-500 ">
                Enter
              </p>
            )}
          </div>
        )}
        {!isWorkspaceConsolidationEnabled && (
          <EditableField
            className="name-header"
            readOnly={readOnly}
            asHeader="h1"
            placeholder={runName}
            showEditIcon
            value={run.displayName ?? ''}
            save={async value => {
              const result = await updateRun({displayName: value});
              if (result.success) {
                window.analytics?.track('Run name edited');
              } else {
                toast.error(result.error);
              }
            }}
          />
        )}
        <div className="name-header-right">
          <RunOverflowMenu
            customRunName={customRunName}
            entityName={entityName}
            projectName={projectName}
            runName={runName}
            run={runFromProps}
            project={projectFromProps}
            readOnly={readOnly}
          />
        </div>
      </div>
    </div>
  );
};

export default RunPageHeader;
